// ── PARENT: RE-ENROLLMENT (priority window) ────────────────────────────
// esc and ageAsOfSept1/seasonLabel (shared classic scripts) stay as bare
// globals — not migrated yet.
//
// Every state-changing action here (reenrollToggle/reenrollSetProgram/
// reenrollSetBilling/confirmReenroll) calls refresh(), which always fully
// re-renders VIEWS['p-reenroll'] through route()/VIEW_INIT — unlike other
// domains in this migration, nothing here bypasses that lifecycle with a
// direct DOM rewrite, so a single VIEW_INIT['p-reenroll'] covers every case.
// confirmReenroll()'s own modal wires its Confirm button directly inline
// (imperative function with control over the modal DOM it just wrote).
import { DB, session, PROGRAMS } from '../core/state.js';
import { apiFetch } from '../core/api.js';
import { isContinuingPerformer, eligiblePrograms, currentSeasonProgram, priorityWindowOpen, priorityDeadlineLabel, programName } from '../core/domain-utils.js';
import { openModal, closeModal, toast } from '../core/ui.js';
import { VIEWS, VIEW_INIT, route, refresh } from '../core/routing.js';
import { programSeasonTotal, showChargeError } from './billing-shared.js';

let reenrollState = {};  // performerId → { selected, programId }

VIEWS['p-reenroll'] = () => {
  if (!priorityWindowOpen()) {
    return `<div class="page-head"><h2 class="serif">Re-enrollment</h2></div>
      <div class="card"><p class="empty">The priority registration window is not currently open.</p></div>`;
  }
  const upSeason = DB.settings.upcomingSeasonYear;
  const kids = DB.children.filter(c => c.accountId === session.id);
  const continuingKids = kids.filter(k => isContinuingPerformer(k.id));

  // Initialize state for each continuing performer, reflecting any prior decision this season
  continuingKids.forEach(k => {
    if (!reenrollState[k.id]) {
      const eligible = eligiblePrograms(k, upSeason);
      const carried = currentSeasonProgram(k.id);
      let defaultProg = eligible.find(p => p.id === carried)?.id || (eligible[0] ? eligible[0].id : null);
      // Prior decision: explicitly declined → unselected. Already registered
      // (an earlier submit, or enrolled by the studio) → shown as done, not
      // offered again: the server leaves a registered performer as they are,
      // and counting them would put their fee in a total nobody is charged.
      const prior = DB.seasonEnrollments.find(se => se.performerId === k.id && se.seasonYear === upSeason
        && PROGRAMS.find(p => p.id === se.programId)?.billingType !== 'workshop');
      const registered = prior && prior.status === 'registered';
      reenrollState[k.id] = {
        locked: registered,
        selected: prior ? !registered && prior.status !== 'declined' : true,
        programId: (prior && prior.status !== 'declined' && prior.programId) || defaultProg,
        billingMode: (prior && prior.billingMode) || 'monthly'
      };
    }
  });

  if (!continuingKids.length) {
    return `<div class="page-head"><span class="eyebrow" style="cursor:pointer" onclick="route('p-home')">‹ Dashboard</span><h2 class="serif">Re-enrollment</h2></div>
      <div class="card"><p class="empty">You don't have any continuing performers eligible for priority registration. New registrations open during open enrollment.</p></div>`;
  }

  // Block registration if the account has any outstanding balance
  const dueBalance = DB.charges
    .filter(c => c.accountId === session.id && c.status === 'due')
    .reduce((s, c) => s + c.amount, 0);
  if (dueBalance > 0) {
    return `
      <div class="page-head">
        <span class="eyebrow check-row" onclick="route('p-home')">‹ Dashboard</span>
        <h2 class="serif">${seasonLabel(upSeason)} <em>Registration</em></h2>
      </div>
      <div class="card" style="border-color:rgba(248,113,113,0.3)">
        <div class="card-hd" style="color:#f87171">Outstanding balance</div>
        <p style="font-size:14px;color:var(--cream);margin-bottom:8px">Your account has an outstanding balance of <b>${fmt$(dueBalance)}</b>.</p>
        <p class="card-intro">Please pay your balance in full before registering for the ${seasonLabel(upSeason)} season. Once it's settled, you can return here to complete registration.</p>
        <button class="btn" style="width:auto;padding:11px 24px" onclick="route('p-billing')">Go to Billing</button>
      </div>`;
  }

  // Compute total materials fees for selected performers
  let total = 0;
  continuingKids.forEach(k => {
    const st = reenrollState[k.id];
    if (st.selected && st.programId) {
      const p = PROGRAMS.find(x => x.id === st.programId);
      if (p && p.materialsFee) total += p.materialsFee;
    }
  });

  const card = session.card ? `${esc(session.card.brand)} ending in ${esc(session.card.last4)}` : null;

  return `
    <div class="page-head">
      <span class="eyebrow check-row" onclick="route('p-home')">‹ Dashboard</span>
      <h2 class="serif">${seasonLabel(upSeason)} <em>Registration</em></h2>
    </div>
    <div class="grid-2" style="align-items:start">
      <div class="card">
        <div class="card-hd">Returning performers</div>
        <p class="card-intro">Confirm which children are returning for the ${seasonLabel(upSeason)} season. Program placement is based on their age as of September 1, ${upSeason}.${priorityDeadlineLabel() ? ` Please register by <b style="color:var(--cream)">${priorityDeadlineLabel()}</b>.` : ''}</p>
        ${continuingKids.map(k => {
          const st = reenrollState[k.id];
          if (st.locked) {
            return `
          <div style="padding:16px 0;border-bottom:1px solid var(--border-soft)">
            <div style="font-size:15px;color:var(--cream)">${esc(k.name)}</div>
            <div style="font-size:12px;color:var(--gold-light);margin-top:2px">Registered for ${esc(programName(st.programId))} ✓</div>
          </div>`;
          }
          const age = ageAsOfSept1(k.dob, upSeason);
          const eligible = eligiblePrograms(k, upSeason);
          const carried = currentSeasonProgram(k.id);
          const carriedProg = PROGRAMS.find(p => p.id === carried);
          const isBBCarried = carriedProg && carriedProg.name && carriedProg.name.toLowerCase().includes('broadway bound');
          const isGraduating = isBBCarried && !eligible.some(p => p.id === carried);
          const hasChoice = eligible.length > 1;
          return `
          <div style="padding:16px 0;border-bottom:1px solid var(--border-soft)">
            <div style="display:flex;align-items:flex-start;gap:12px">
              <input type="checkbox" ${st.selected ? 'checked' : ''} data-action="reenroll-toggle" data-performer="${k.id}" style="width:auto;margin-top:3px;accent-color:var(--gold)">
              <div style="flex:1">
                <div style="font-size:15px;color:var(--cream)">${esc(k.name)}</div>
                <div style="font-size:12px;color:var(--muted);margin-top:2px">Will be ${age} on Sept 1${carried ? ' · completed ' + esc(programName(carried)) : ''}</div>
                ${st.selected ? `
                  ${isGraduating ? `<div style="font-size:12px;color:var(--gold);margin-top:6px;font-style:italic">Graduating from Broadway Bound into Performance Company</div>` : ''}
                  ${hasChoice ? `
                    <div style="margin-top:10px">
                      <div style="font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:0.1em;margin-bottom:6px">Choose program</div>
                      ${eligible.map(p => `
                        <label style="display:flex;align-items:center;gap:8px;padding:7px 10px;border-radius:6px;border:1px solid ${st.programId === p.id ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${st.programId === p.id ? 'rgba(201,168,76,0.06)' : 'transparent'};cursor:pointer;margin-bottom:6px">
                          <input type="radio" name="prog_${k.id}" ${st.programId === p.id ? 'checked' : ''} data-action="reenroll-set-program" data-performer="${k.id}" data-program="${p.id}" style="width:auto;accent-color:var(--gold)">
                          <span style="flex:1;font-size:13px;color:var(--cream)">${esc(p.name)}</span>
                          <span style="font-size:12px;color:var(--gold-light)">${p.materialsFee ? fmt$(p.materialsFee) + ' materials' : 'No fee'}</span>
                        </label>`).join('')}
                    </div>` : `
                    <div style="margin-top:8px;display:flex;justify-content:space-between;align-items:center;padding:8px 10px;border-radius:6px;background:rgba(201,168,76,0.05);border:1px solid var(--border-soft)">
                      <span style="font-size:13px;color:var(--cream)">${eligible[0] ? esc(eligible[0].name) : 'No eligible program'}</span>
                      <span style="font-size:12px;color:var(--gold-light)">${eligible[0] && eligible[0].materialsFee ? fmt$(eligible[0].materialsFee) + ' materials' : ''}</span>
                    </div>`}
                  ${(() => {
                    const sp = PROGRAMS.find(x => x.id === st.programId);
                    if (!sp || !sp.allowAdvance) return '';
                    const multiSem = sp.semesters && sp.semesters.length > 1;
                    const advLabel = multiSem
                      ? `Pay by semester (${fmt$(sp.monthly * (sp.semesters[0].months || []).length)}/semester)`
                      : `Pay the full season up front (${fmt$(programSeasonTotal(sp))})`;
                    const monLabel = `Pay monthly (${fmt$(sp.monthly)}/mo)`;
                    const opt = (mode, label, sub) => `
                      <label style="display:flex;align-items:flex-start;gap:8px;padding:8px 10px;border-radius:6px;border:1px solid ${st.billingMode === mode ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${st.billingMode === mode ? 'rgba(201,168,76,0.06)' : 'transparent'};cursor:pointer;margin-bottom:6px">
                        <input type="radio" name="bill_${k.id}" ${st.billingMode === mode ? 'checked' : ''} data-action="reenroll-set-billing" data-performer="${k.id}" data-mode="${mode}" style="width:auto;margin-top:2px;accent-color:var(--gold)">
                        <span style="flex:1"><span style="font-size:13px;color:var(--cream)">${label}</span>${sub ? `<div style="font-size:11px;color:var(--muted);margin-top:1px">${sub}</div>` : ''}</span>
                      </label>`;
                    return `
                    <div style="margin-top:12px">
                      <div style="font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:0.1em;margin-bottom:6px">How would you like to pay tuition?</div>
                      ${opt('monthly', monLabel, 'Billed on the 1st of each month during the season')}
                      ${opt('advance', advLabel, multiSem ? 'Each semester is billed at its start, so a December break never needs a refund' : 'One payment covers the whole season')}
                      <div style="font-size:11px;color:var(--muted);font-style:italic;margin-top:2px">Tuition starts when the season begins — nothing is charged today beyond materials fees.</div>
                    </div>`;
                  })()}
                ` : ''}
              </div>
            </div>
          </div>`;
        }).join('')}
      </div>

      <div class="card">
        <div class="card-hd">Confirm &amp; pay</div>
        <div style="display:flex;flex-direction:column;gap:10px;margin-bottom:16px">
          ${continuingKids.filter(k => reenrollState[k.id].selected && reenrollState[k.id].programId).map(k => {
            const p = PROGRAMS.find(x => x.id === reenrollState[k.id].programId);
            const mode = reenrollState[k.id].billingMode || 'monthly';
            const modeLabel = !p.allowAdvance ? '' : (mode === 'advance'
              ? (p.semesters && p.semesters.length > 1 ? `${fmt$(p.monthly * (p.semesters[0].months || []).length)}/semester` : `${fmt$(programSeasonTotal(p))}/season`)
              : `${fmt$(p.monthly)}/mo`);
            return `<div style="display:flex;justify-content:space-between;font-size:13px">
              <div><div style="color:var(--cream)">${esc(k.name)}</div><div style="font-size:11px;color:var(--muted)">${esc(p.name)}${modeLabel ? ' · ' + modeLabel : ''} · materials fee</div></div>
              <div style="color:var(--gold-light)">${p.materialsFee ? fmt$(p.materialsFee) : '—'}</div>
            </div>`;
          }).join('') || '<p class="empty" style="padding:8px 0">No performers selected.</p>'}
        </div>
        <hr style="border:none;border-top:1px solid var(--border-soft);margin:0 0 14px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
          <span style="font-size:14px;color:var(--cream)">Total due today</span>
          <span style="font-family:'Libre Baskerville',serif;font-size:26px;font-weight:700;color:var(--cream)">${fmt$(total)}</span>
        </div>
        ${card
          ? `<div style="font-size:12px;color:var(--muted);margin-bottom:14px">Charged to your card on file (${esc(session.card.brand)} ending in ${esc(session.card.last4)})</div>
             <button class="btn" data-action="confirm-reenroll" ${total === 0 && continuingKids.every(k=>!reenrollState[k.id].selected) ? 'disabled' : ''}>Confirm &amp; pay ${fmt$(total)}</button>`
          : `<div style="font-size:12px;color:var(--red);margin-bottom:14px">No card on file. Add one in Billing first.</div>
             <button class="btn" disabled>Confirm &amp; pay</button>`}
        <p style="font-size:11px;color:var(--muted);text-align:center;margin-top:10px;line-height:1.6">Your season contract will be sent separately before the season begins. Materials fees reserve your performer's spot.</p>
      </div>
    </div>`;
};
VIEW_INIT['p-reenroll'] = () => {
  document.querySelectorAll('[data-action="reenroll-toggle"]').forEach(el => {
    el.addEventListener('change', () => reenrollToggle(el.dataset.performer));
  });
  document.querySelectorAll('[data-action="reenroll-set-program"]').forEach(el => {
    el.addEventListener('change', () => reenrollSetProgram(el.dataset.performer, el.dataset.program));
  });
  document.querySelectorAll('[data-action="reenroll-set-billing"]').forEach(el => {
    el.addEventListener('change', () => reenrollSetBilling(el.dataset.performer, el.dataset.mode));
  });
  document.querySelectorAll('[data-action="confirm-reenroll"]').forEach(btn => {
    btn.addEventListener('click', confirmReenroll);
  });
};

function reenrollToggle(performerId) {
  reenrollState[performerId].selected = !reenrollState[performerId].selected;
  refresh();
}
function reenrollSetProgram(performerId, programId) {
  reenrollState[performerId].programId = programId;
  reenrollState[performerId].billingMode = 'monthly';  // reset; advance differs per program
  refresh();
}
function reenrollSetBilling(performerId, mode) {
  reenrollState[performerId].billingMode = mode;
  refresh();
}
function confirmReenroll() {
  const upSeason = DB.settings.upcomingSeasonYear;
  // Safety net: never allow registration while a balance is owed
  const dueBalance = DB.charges
    .filter(c => c.accountId === session.id && c.status === 'due')
    .reduce((s, c) => s + c.amount, 0);
  if (dueBalance > 0) { toast('Please pay your outstanding balance before registering'); refresh(); return; }
  const kids = DB.children.filter(c => c.accountId === session.id).filter(k => isContinuingPerformer(k.id));
  const selected = kids.filter(k => reenrollState[k.id] && reenrollState[k.id].selected && reenrollState[k.id].programId);
  if (!selected.length) { toast('Select at least one performer to register'); return; }

  let total = 0;
  selected.forEach(k => {
    const p = PROGRAMS.find(x => x.id === reenrollState[k.id].programId);
    if (p && p.materialsFee) total += p.materialsFee;
  });

  // Confirmation modal, then Stripe-gated charge
  const cardRef = session.card ? ` to your ${esc(session.card.brand)} ending in ${esc(session.card.last4)}` : ' to your card on file';
  openModal(`
    <h3>Confirm registration</h3>
    <p class="card-intro">This will register ${selected.length} performer${selected.length > 1 ? 's' : ''} for the ${seasonLabel(upSeason)} season and charge <b style="color:var(--cream)">${fmt$(total)}</b> in materials fees${cardRef}.</p>
    <div style="display:flex;gap:10px">
      <button class="btn" id="reenrollConfirmBtn" style="flex:1">Confirm — charge ${fmt$(total)}</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('reenrollConfirmBtn').addEventListener('click', processReenroll);
}
async function processReenroll() {
  const btn = document.getElementById('reenrollConfirmBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Processing…'; }
  const upSeason = DB.settings.upcomingSeasonYear;
  const kids = DB.children.filter(c => c.accountId === session.id).filter(k => isContinuingPerformer(k.id));
  const selected = kids.filter(k => reenrollState[k.id] && reenrollState[k.id].selected && reenrollState[k.id].programId);

  const selections = selected.map(k => ({
    performerId: k.id,
    programId: reenrollState[k.id].programId,
    billingMode: reenrollState[k.id].billingMode || 'monthly'
  }));

  try {
    await apiFetch('/enrollments/reenroll', {
      method: 'POST', body: JSON.stringify({ selections, seasonYear: upSeason })
    });
    // Reload charges and enrollments
    DB.charges = await apiFetch('/charges?accountId=' + session.id);
    DB.seasonEnrollments = (await apiFetch('/enrollments')).map(se => ({
      id: se.id, performerId: se.performerId, programId: se.programId,
      seasonYear: se.seasonYear, status: se.status, billingMode: se.billingMode,
      rateOverride: se.rateOverride ?? null,
      registeredAt: se.createdAt, materialsFeePaid: false, contractSent: false, contractSigned: false
    }));
    reenrollState = {};
    closeModal();
    route('p-reenroll-done');
  } catch (e) {
    showChargeError(e.message || 'Could not reach the payment processor. Please try again.');
  }
}

VIEWS['p-reenroll-done'] = () => {
  const upSeason = DB.settings.upcomingSeasonYear;
  return `
    <div class="page-head"><h2 class="serif">Registration <em>Confirmed</em></h2></div>
    <div class="card" style="max-width:520px">
      <div style="text-align:center;padding:20px 0">
        <div style="font-size:40px;color:var(--gold);margin-bottom:14px">✦</div>
        <div style="font-family:'Libre Baskerville',serif;font-size:20px;color:var(--cream);margin-bottom:10px">You're registered for ${seasonLabel(upSeason)}</div>
        <p style="font-size:14px;color:var(--muted);line-height:1.7;max-width:380px;margin:0 auto">Your materials fees have been paid and your performers' spots are reserved. Your season contract will be sent to you separately before the season begins in September.</p>
        <button class="btn" style="width:auto;margin-top:20px;padding:11px 24px" onclick="route('p-home')">Back to dashboard</button>
      </div>
    </div>`;
};
